import {all, call, put, select} from 'redux-saga/effects';
import {equals, filter, isEmpty} from 'ramda';
import {
    sendMobileAnalyticsEvent,
    CATEGORY as CATEGORY_ANALYTICS,
} from '@optimaxdev/analytics/mobile';

import {FilterType} from 'constants/flow/flowTypes';
import {ActiveFiltersType} from 'reducers/category/activeFilters/activeFiltersType';
import {setActiveFilters, setPageNumber} from 'actions/category/category';
import flags from 'constants/flags/flags';
import {getActiveFilters, getFilters} from 'selectors/filters/filters';
import {updateUrlFilters, updateCategoryItem} from 'sagas/category/filters/filters';

import {MyFitFiltersType, scrollCTPafterMyFit, setMyFitState} from '../reducers/myFit';
import {getMyFitFilters, getMyFitIsEnabled} from '../selectors/myFit';
import {CATEGORIES_WITH_HARDCODED_FILTERS, mapFilters} from './utils';
import {checkIsMyFitPreset} from './myFit';

/**
 * Update active filters according the My Fit filters
 * @param {MyFitFiltersType} filters - updated filters
 */
export function* updateFilters(filters: MyFitFiltersType) {
    const [isMyFitEnabled, activeFilters, allFilters]: [
        boolean,
        ActiveFiltersType,
        FilterType,
    ] = yield all([select(getMyFitIsEnabled), select(getActiveFilters), select(getFilters)]);

    const newFilters: ActiveFiltersType = yield call(mapFilters, {
        filters,
        allFilters,
        isMyFitEnabled,
        activeFilters,
    });

    yield all([
        put(scrollCTPafterMyFit()),
        put(setActiveFilters(filter(item => !isEmpty(item), newFilters))),
        put(setPageNumber(1)),
        call(updateCategoryItem, newFilters),
        call(updateUrlFilters, newFilters),
    ]);
}

/**
 * Handle filter change
 */
export function* handleFilterChange() {
    if (!flags.myFit.isEnabled()) return;

    const [myFitFilters, activeFilters, isMyFitEnabled]: [
        MyFitFiltersType,
        ActiveFiltersType,
        boolean,
    ] = yield all([select(getMyFitFilters), select(getActiveFilters), select(getMyFitIsEnabled)]);

    const activeFiltersById = Object.entries(activeFilters).reduce(
        (acc: Record<string, number[]>, [key, activeFilter]) => ({
            ...acc,
            [key]: activeFilter.map(({value_id: id}) => id),
        }),
        {},
    );

    if (equals(CATEGORIES_WITH_HARDCODED_FILTERS[window.location.pathname], myFitFilters.gender)) {
        activeFiltersById.gender = myFitFilters.gender;
    }

    yield call(checkIsMyFitPreset, myFitFilters, activeFiltersById, isMyFitEnabled);
}

/**
 * Handle clear all filters button
 */
export function* clearFilters() {
    if (!flags.myFit.isEnabled()) return;

    const isMyFitEnabled: boolean = yield select(getMyFitIsEnabled);

    if (!isMyFitEnabled) return;

    yield call(
        sendMobileAnalyticsEvent,
        CATEGORY_ANALYTICS.MY_FIT,
        'Filters Section - State Off - Changed by Clear',
    );

    yield put(setMyFitState(false));
}
